/* The sub-task inspector (4.2), and SubtaskActions — the verdict bar (6.1, 6.2)
 * that Workspace renders BELOW this panel rather than inside it.
 *
 * EVERY EDIT RETURNS THE SUB-TASK TO PENDING (4.2.7) — description, fields,
 * hands, title, timing. It is applied optimistically in useJob so that it is
 * impossible to add a new edit control here and forget it, and enforced in
 * services._reopen, which is the copy that decides. The server's half was
 * missing until testing noticed the verdict buttons appearing for one debounce
 * interval and disappearing again: the optimistic PENDING was being overwritten
 * by a PATCH response that still said APPROVED.
 *
 * NO "CHECK WITH AI", NO "WHY THIS IS FLAGGED" — removed at testing's
 * request, 24 Sep, along with the checklist bar and the shortcut row under the
 * verdict buttons, so the right-hand column fits without scrolling. The ▲ / ✎
 * badge in the header still says WHAT the judge decided. The judge's reasoning
 * is still in the API (`/subtasks/{id}/evidence`, `verdict.reason_codes`) and
 * `lib/reasons.ts` still turns it into sentences, so either panel can come
 * back without touching the pipeline.
 */

import { useEffect, useState } from "react";
import type { RefObject } from "react";
import type { GtMatch, Guideline, Hands, Subtask, SubtaskPatch } from "../api/types";
import { REJECTION_REASONS } from "../api/types";
import { HAND_GLYPH, HAND_LABEL, HAND_SHORT, preciseClock, subtaskTitle }
  from "../lib/format";
import { Button, Eyebrow, VerdictBadge } from "./ui";

const HANDS: Hands[] = ["left", "both", "right", "idle"];

interface Props {
  sub: Subtask;
  siblings: Subtask[];
  index: number;
  guideline: Guideline;
  playhead: number;
  onEdit: (patch: SubtaskPatch) => void;
  onNudgeBoundary: (edge: "start" | "end", to: number) => void;
  /** Still here: the rejected-sub-task banner offers its own Re-open, which is
   *  the one that sits next to the reason being undone. */
  onReopen: () => void;
  onPrev: () => void;
  onNext: () => void;
  descriptionRef: RefObject<HTMLTextAreaElement | null>;
  readOnly?: boolean;
  /** QA only: LLM-1's label against the ground truth (the Accuracy page's verdict for this row). */
  gtMatch?: GtMatch;
}

const GT_PILL: Record<GtMatch, { text: string; cls: string; title: string }> = {
  YES: { text: "GT ✓", cls: "bg-verdict-approved-bg text-verdict-approved-ink",
         title: "LLM-1's label matches the ground truth" },
  NO: { text: "GT ✗", cls: "bg-verdict-rejected-bg text-verdict-rejected-ink",
        title: "LLM-1's label does not match the ground truth" },
  AMBIGUOUS: { text: "GT ½", cls: "bg-verdict-flagged-bg text-verdict-flagged-ink",
               title: "The frame check could not settle it — counted as half wrong" },
  NO_GT: { text: "no GT", cls: "bg-ground text-muted",
           title: "No ground-truth event (the crowd merged this sub-task into a neighbour)" },
};

export function Inspector(p: Props) {
  // `guideline` is still passed in but no longer read here — the taxonomy
  // suggestion lists it fed were removed (see Field).
  const { sub, siblings, index, readOnly } = p;
  const [renaming, setRenaming] = useState(false);

  const isFirst = index === 0;
  const isLast = index === siblings.length - 1;

  // flex-[2] against the video column's flex-[3] — the 60/40 split testing
  // asked for. It was a fixed w-96, which is 40% of one window width and a
  // quarter of the screen at another.
  return (
    // gap-2.5, not gap-5, and gap-y-2.5 in the field grid — testing, 24 Sep: no
    // scrollbar in the right-hand pane. overflow-y-auto stays as the fallback
    // for a genuinely short window, where clipping a control would be worse
    // than a scrollbar; at normal heights it no longer engages.
    <aside className="flex min-h-0 min-w-0 flex-1 flex-col gap-2.5
      overflow-y-auto rounded-panel bg-card shadow-card p-3 border border-line">

      {/* ------------------------------------------------------------ head
          ONE LINE, THE SAME SHAPE AS THE TASK CARD OPPOSITE IT. The label sat
          on its own row above the title, so the two headers started on the same
          line and then disagreed about everything after it: TASK read
          "TASK · title · 1 of 4 · status" across one row while SUB-TASK spent a
          row on the word SUB-TASK and put its title underneath. Same order as
          the card now — label, title, edit, position, status. */}
      <header className="flex items-center gap-2">
        <Eyebrow className="shrink-0">Sub-task</Eyebrow>

        {renaming ? (
          <input
            autoFocus
            defaultValue={sub.custom_title ?? subtaskTitle(sub, siblings)}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v) p.onEdit({ custom_title: v });
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setRenaming(false);
            }}
            className="min-w-0 flex-1 rounded-field bg-ground px-2 py-1 text-[15px] border border-line"
          />
        ) : (
          // THE WHOLE TITLE, WRAPPED — manager's review, 27 Oct: titles were cut
          // to "place do…". `truncate` is gone; a long title takes a second
          // line instead. The edited flag lives in the tooltip now (see below).
          <h2 className="min-w-0 flex-1 break-words text-[15px] font-extrabold leading-snug tracking-[-0.01em] text-ink"
            title={sub.edited
              ? `${subtaskTitle(sub, siblings)} — edited, differs from the AI original`
              : subtaskTitle(sub, siblings)}>
            {subtaskTitle(sub, siblings)}
          </h2>
        )}

        {!readOnly && (
          <button onClick={() => setRenaming(true)} title="Rename this sub-task"
            className="shrink-0 rounded-chip px-1 text-muted hover:text-ink">✎</button>
        )}
        {!readOnly && sub.custom_title && (
          <button onClick={() => p.onEdit({ custom_title: null })}
            title="Back to the automatic verb · object title"
            className="shrink-0 rounded-chip px-1 text-muted hover:text-ink">↺</button>
        )}

        {/* No "edited" word and no status pill here — manager's review, 27 Oct:
            both took header space from the title. The status still shows on
            the task strip box and in the verdict buttons below; "edited" is in
            the title's tooltip. */}
        {sub.verdict && (
          <VerdictBadge decision={sub.verdict.decision} score={sub.verdict.trust_score} />
        )}
        {p.gtMatch && (
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${GT_PILL[p.gtMatch].cls}`}
            title={GT_PILL[p.gtMatch].title}>{GT_PILL[p.gtMatch].text}</span>
        )}

        {/* Position and the ← / → that change it, together — they are one
            control, and the count between the arrows is what they act on.
            ← / → and not ↑ / ↓: sub-tasks run left-to-right along the timeline
            directly below, so vertical arrows pointed across the one axis they
            do not move on. */}
        <span className="flex shrink-0 items-center gap-1 text-[13px] text-muted">
          <button onClick={p.onPrev} disabled={isFirst}
            className="rounded-chip bg-ground px-2 py-0.5 hover:bg-line disabled:opacity-30"
            title="Previous sub-task (J)">←</button>
          <span className="px-0.5">{index + 1} of {siblings.length}</span>
          <button onClick={p.onNext} disabled={isLast}
            className="rounded-chip bg-ground px-2 py-0.5 hover:bg-line disabled:opacity-30"
            title="Next sub-task (K)">→</button>
        </span>
      </header>

      {/* 4.2.8 — a rejected sub-task shows its reason with one-click re-open */}
      {sub.status === "REJECTED" && sub.rejection_reason && (
        <div className="flex items-center justify-between gap-2 rounded-field
          bg-verdict-rejected-bg px-3 py-2 text-[13px] ring-1 ring-verdict-rejected-line">
          <span className="text-verdict-rejected-ink">
            Rejected — {sub.rejection_reason}
          </span>
          {!readOnly && (
            <button onClick={p.onReopen}
              className="shrink-0 rounded-chip bg-ground px-2 py-0.5 hover:bg-line">
              Re-open
            </button>
          )}
        </div>
      )}

      {/* NO "Why the AI labelled it this way". `reason` and `ai_confidence` are
          OURS: the field the prompt-improvement loop reads when the judge flags
          a false positive. On the review screen they invite the annotator to
          argue with the model's own account of itself instead of watching the
          video, and a confidence number beside a label reads as a score the
          reviewer should defer to. Removed at testing's request; both fields are
          still in the artifact and still in the API. */}

      {/* --------------------------------------------------- description */}
      <label className="flex flex-col gap-1">
        {/* No "What the person does · present tense" heading — manager's
            review, 27 Oct: it took a row. The textarea carries the same words
            as its accessible name and as the placeholder when empty. */}
        <DescriptionBox
          ref={p.descriptionRef}
          value={sub.description}
          disabled={readOnly}
          onChange={(v) => p.onEdit({ description: v })}
        />
      </label>

      {/* ------------------------------------------------------- fields */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-2.5">
        <Field label="Verb" hint="action" value={sub.verb}
          disabled={readOnly}
          onChange={(v) => p.onEdit({ verb: v })} />
        <Field label="Object" hint="acted on" value={sub.object_name}
          disabled={readOnly}
          onChange={(v) => p.onEdit({ object_name: v })} />
        <Field label="Tool" hint="used with" value={sub.tool}
          disabled={readOnly}
          onChange={(v) => p.onEdit({ tool: v })} />
        <Field label="Target" hint="ends at" value={sub.target}
          disabled={readOnly}
          onChange={(v) => p.onEdit({ target: v })} />
      </div>

      {/* -------------------------------------------------------- hands */}
      <div className="flex flex-col gap-1">
        <Eyebrow compact>Hands</Eyebrow>
        {/* A GLYPH OVER THE WORD, and one joined control — v1.6's `.seg`, and
            testing asked for the icons on 17 Sep.

            ONE JOINED GROUP, not four gapped chips. Four separated buttons read
            as four independent toggles you might set in combination; hands is
            one value with four choices, and a segmented control is the shape
            that says so. Hairline dividers between, rounded only at the ends.

            The glyphs are the deck's `HANDICON` map copied value for value into
            `HAND_GLYPH` — 🫲 leftwards hand, 🫱 rightwards hand, 🙌 both, a dot
            for idle. Mirrored hands, so the icon says which side on its own.
            The timeline blocks draw the same map, so the panel and the lane
            below it cannot disagree about what a hand looks like. `font-emoji`
            is the fallback chain. See the note on HAND_GLYPH. */}
        <div className="flex overflow-hidden rounded-field border border-line">
          {HANDS.map((h, i) => (
            <button
              key={h}
              disabled={readOnly}
              onClick={() => p.onEdit({ hands: h })}
              title={HAND_LABEL[h]}
              // Glyph BESIDE the word, not over it (24 Sep): one line instead
              // of two is ~14px of a column that must not scroll.
              className={`flex flex-1 items-center justify-center gap-1.5 py-1.5
                text-[13px] leading-none transition duration-150 ease-settle
                ${i > 0 ? "border-l border-line" : ""}
                ${sub.hands === h
                  ? "bg-teal text-white"
                  : "bg-card text-text2 hover:bg-ground"}
                disabled:opacity-40`}
            >
              <span aria-hidden className="font-emoji text-[15px] leading-none">
                {HAND_GLYPH[h]}
              </span>
              <span className="font-semibold">{HAND_SHORT[h]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------- timing */}
      <div className="grid grid-cols-3 gap-2 text-[13px]">
        <Stepper label="Start" value={sub.start_seconds}
          disabled={readOnly || isFirst}
          pinnedHint={isFirst ? "pinned to the task's start" : undefined}
          onNudge={(d) => p.onNudgeBoundary("start", sub.start_seconds + d)}
          onSetToPlayhead={() => p.onNudgeBoundary("start", p.playhead)} />
        <Stepper label="End" value={sub.end_seconds}
          disabled={readOnly || isLast}
          pinnedHint={isLast ? "pinned to the task's end" : undefined}
          onNudge={(d) => p.onNudgeBoundary("end", sub.end_seconds + d)}
          onSetToPlayhead={() => p.onNudgeBoundary("end", p.playhead)} />
        <div className="flex flex-col gap-1">
          <Eyebrow compact>Length</Eyebrow>
          {/* Always derived, never edited — 4.2.6.
              preciseClock, matching Start and End either side of it: this read
              "2.80s" between 00:00.000 and 00:02.800, so the one number you
              would subtract the other two to check was written in a notation
              neither of them used. */}
          <span className="rounded-chip bg-ground px-2 py-1.5 font-mono text-text2">
            {preciseClock(sub.end_seconds - sub.start_seconds)}
          </span>
        </div>
      </div>

    </aside>
  );
}

/* ------------------------------------------------------- the verdict bar */

/** Approve & next, Reject, Reset to AI — OUTSIDE the sub-task panel.
 *
 *  Testing asked for this and it is the right call: these act on the sub-task
 *  but they are not part of describing it. Inside the panel they sat below the
 *  timing boxes, so the fields you fill in and the decision you make about them
 *  were one continuous scroll — and on a long panel the decision scrolled away
 *  from the thing being decided. Their own bar under the panel keeps them in
 *  the same place on every sub-task.
 *
 *  ONE ROW OF THREE — testing, 24 Sep. "Check with AI" is gone, and Reset to AI
 *  moved up from a caption line underneath into the slot it left, beside
 *  Approve and Reject: it is the third thing you can do to this sub-task, and a
 *  row of equals says so where a small centred link read as an afterthought. It
 *  also takes a line of height out of a column that must not scroll.
 *
 *  THE REASON MODAL IS THE EXCEPTION, and it is controlled from Workspace.
 *  7.1 gives R to "reject", and R used to answer with a toast telling the
 *  annotator to go and find the button — a shortcut whose whole effect was to
 *  describe the thing it should have done. The keyboard handler lives in
 *  Workspace, so the open/closed state has to as well; keeping it local here is
 *  what made R unimplementable in the first place. */
export function SubtaskActions(
  { sub, onApprove, onReject, onReopen, onResetToAi, readOnly,
    rejecting, onRejecting }: {
    sub: Subtask; readOnly?: boolean;
    onApprove: () => void; onReject: (reason: string) => void;
    onReopen: () => void; onResetToAi: () => void;
    rejecting: boolean; onRejecting: (v: boolean) => void;
  },
) {
  const setRejecting = onRejecting;

  if (readOnly) return null;

  // The same control in both states, so it never moves under the cursor.
  const resetToAi = (
    <Button onClick={onResetToAi} title="Restore the model's original labels">
      ↺ Reset to AI
    </Button>
  );

  return (
    <div className="shrink-0 rounded-panel bg-card shadow-card p-3 border border-line">
      {/* A DECIDED SUB-TASK SHOWS ITS DECISION AND A WAY BACK, not the two
          buttons that made it. Leaving Approve / Reject live on an already
          approved sub-task offers an action with no visible effect and no
          record that anything happened. The verdict button is inert — a label
          in button's clothing, so the row keeps its shape — and Re-open is the
          live control beside it.

          Editing puts it back to PENDING and Approve / Reject return, which is
          true on the server as well as here (services._reopen). */}
      {sub.status !== "PENDING" ? (
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={sub.status === "APPROVED" ? "approve" : "reject"}
            disabled
            title={sub.status === "APPROVED"
              ? "Already approved — Re-open to change it"
              : "Already rejected — Re-open to change it"}
          >
            {sub.status === "APPROVED" ? "✓ Approved" : "✕ Rejected"}
          </Button>
          <Button onClick={onReopen} title="Return this sub-task to pending">
            ↺ Re-open
          </Button>
          {resetToAi}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <Button variant="approve" onClick={onApprove} title="A">
            ✓ Approve &amp; next
          </Button>
          {/* ✕, not ⛔. A crossed circle reads as "forbidden" — the control is
              unavailable — where this button is available and is the whole
              point of the screen. ✕ is "reject this one". */}
          <Button variant="reject" onClick={() => setRejecting(true)} title="R">
            ✕ Reject
          </Button>
          {resetToAi}
        </div>
      )}

      {/* 6.2.2 — the reason picker, as a modal. It used to replace the
          Approve/Reject pair in place, so the buttons vanished and a list
          appeared where they had been: the screen changed under the cursor and
          nothing said the video was still there behind it. */}
      {rejecting && (
        <RejectModal
          onPick={(r) => { onReject(r); setRejecting(false); }}
          onCancel={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

function RejectModal(
  { onPick, onCancel }: { onPick: (r: string) => void; onCancel: () => void },
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-modal bg-card p-5
        shadow-raised ring-1 ring-line">
        <h3 className="text-lg font-extrabold tracking-[-0.01em] text-ink">Reject sub-task</h3>
        <p className="mt-1 text-[13px] text-muted">
          Pick why — the reason goes back to the AI pipeline and the customer.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {REJECTION_REASONS.map((r) => (
            <button
              key={r}
              onClick={() => onPick(r)}
              className="rounded-field border border-line bg-ground px-3 py-2
                text-left text-[15px] text-ink hover:border-verdict-rejected-line
                hover:bg-verdict-rejected-bg"
            >
              {r}
            </button>
          ))}
        </div>
        <div className="mt-4 text-right">
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- fragments */

function Field({ label, hint, value, onChange, disabled }: {
  label: string; hint: string; value: string | null;
  disabled?: boolean;
  onChange: (v: string | null) => void;
}) {
  // A PLAIN TEXT BOX — testing, 24 Sep: Verb, Object, Tool and Target must be
  // text boxes, not drop-downs. This was an <input list> over a <datalist> of
  // the guideline's taxonomy, which browsers draw as a drop-down (an arrow and
  // a suggestion list) even though any value could be typed. The vocabulary is
  // OPEN either way; `autoComplete="off"` also stops the browser's own
  // history drop-down from appearing in its place.
  return (
    <label className="flex flex-col gap-1">
      <Eyebrow compact hint={hint}>{label}</Eyebrow>
      <input
        type="text"
        autoComplete="off"
        value={value ?? ""}
        disabled={disabled}
        placeholder="—"
        /* TRIM ON BLUR, NEVER ON CHANGE — testing, 21 Sep: "writing anything in
           Verb, Object, Tool and Target doesn't allow us to enter space."

           It was `onChange(e.target.value.trim() || null)`. The input is
           controlled, so trimming on every keystroke meant: type "wooden", type
           a space, value becomes "wooden ", trim gives "wooden", state is
           "wooden", the field re-renders WITHOUT the space. The character was
           deleted as fast as it was typed and multi-word values were literally
           impossible to enter.

           That blocks the product's whole point. The taxonomy is full of them —
           "wooden spatula", "allen key set", "meat and onions" — and the only
           way to correct a label to one of those is to type it.

           WHY IT SURVIVED THIS LONG: every value on screen arrives from the
           pipeline, which writes the database directly. The spaces in AI output
           never travel through this input, so the field looked fine until
           somebody edited one.

           Blur is the right moment: it is when the value is finished, and it
           still strips the stray trailing space before it reaches the server.
           Empty stays null rather than "" — the column is nullable and "" would
           be a second way to say the same thing. */
        onChange={(e) => onChange(e.target.value || null)}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          if (trimmed !== e.target.value) onChange(trimmed || null);
        }}
        className="rounded-field bg-ground px-2 py-1 text-[13px] border border-line
          disabled:opacity-40"
      />
    </label>
  );
}

function Stepper({ label, value, onNudge, onSetToPlayhead, disabled, pinnedHint }: {
  label: string; value: number; disabled?: boolean; pinnedHint?: string;
  onNudge: (delta: number) => void; onSetToPlayhead: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {/* No 📌. It marked the two edges that cannot move — the first sub-task's
          Start and the last one's End — but it sat in the label of a control
          that is already visibly disabled, so it annotated a fact the greyed-out
          box was making anyway. The explanation stays as the tooltip. */}
      <Eyebrow compact title={pinnedHint}>{label}</Eyebrow>
      {/* THE CONTROLS LIVE IN THE BOX WITH THE VALUE THEY CHANGE. They were a
          separate row underneath, so on screen the Start field and the End
          field each read as a value, and then there was an unlabelled strip of
          six small buttons below both — nothing tied a button to the number it
          moved. One bordered box per boundary: the time, then its own steppers. */}
      {/* − LEFT OF THE NUMBER, + RIGHT OF IT. Both used to sit to the right,
          labelled "−½s" and "+½s", so the control that makes the time smaller
          was on the side where the time gets larger and each button carried the
          step size in type smaller than the value it changed. Flanking the
          number makes the direction the layout rather than the label.

          The step is still half a second and the tooltips still say so — it is
          the one thing a symbol cannot carry, and it belongs on hover rather
          than in a permanent caption on both buttons. */}
      <div className={`flex items-center gap-1 rounded-field border border-line
        bg-ground px-1.5 py-1 ${disabled ? "opacity-60" : ""}`}>
        <button disabled={disabled} onClick={() => onNudge(-0.5)}
          title="Half a second earlier"
          className="rounded-chip px-1.5 text-[15px] leading-none text-muted hover:bg-line
            hover:text-ink disabled:opacity-30">−</button>
        <span className="flex-1 text-center font-mono text-text2">
          {preciseClock(value)}
        </span>
        <button disabled={disabled} onClick={() => onNudge(0.5)}
          title="Half a second later"
          className="rounded-chip px-1.5 text-[15px] leading-none text-muted hover:bg-line
            hover:text-ink disabled:opacity-30">+</button>
        <button disabled={disabled} onClick={onSetToPlayhead}
          title="Set to the frame now showing"
          className="rounded-chip px-1 text-muted hover:bg-line hover:text-ink
            disabled:opacity-30">⌖</button>
      </div>
    </div>
  );
}

/** TWO LINES, THEN IT SCROLLS. It used to grow to fit its content, which on a
 *  long description pushed the four label fields, the hands row and the timing
 *  boxes down the panel — so typing moved every control the annotator was about
 *  to use, and on the longest descriptions the verdict buttons left the screen
 *  entirely. A fixed two-row box keeps the panel's geometry constant while
 *  typing; the text scrolls inside it, which is the one thing that should move.
 *
 *  This is a plain textarea now. `rows={2}` plus a fixed height IS the whole
 *  behaviour — the auto-grow effect was the feature being removed, and keeping
 *  a component named AutoTextarea that does not auto-grow would be worse than
 *  either. */
function DescriptionBox({ ref, value, onChange, disabled }: {
  ref: RefObject<HTMLTextAreaElement | null>;
  value: string; disabled?: boolean; onChange: (v: string) => void;
}) {
  return (
    <textarea
      ref={ref}
      value={value}
      disabled={disabled}
      rows={2}
      aria-label="What the person does (present tense)"
      placeholder="What the person does, present tense"
      onChange={(e) => onChange(e.target.value)}
      className="h-[3.6rem] resize-none overflow-y-auto rounded-field bg-ground px-2
        py-1.5 text-[13px] leading-relaxed border border-line disabled:opacity-40"
    />
  );
}
